/* Luxe & Gloss responsive quick-shop viewer and retained discovery context. */
(function (root, factory) {
  const api = factory(root)
  if (typeof module === 'object' && module.exports) module.exports = api
  if (root) {
    root.LG = root.LG || {}
    root.LG.quickShop = api
  }
})(typeof window !== 'undefined' ? window : undefined, function (root) {
  'use strict'

  const normalizeProducts = products => {
    if (!Array.isArray(products)) return []
    const seen = new Set()
    return products.flatMap(product => {
      const url = String(product?.url || '').trim()
      const handle = String(product?.handle || '').trim()
      if (!url || !handle || seen.has(url)) return []
      seen.add(url)
      return [{ id: String(product.id || ''), handle, url }]
    })
  }

  const viewerLocation = (locationValue, handle = '') => {
    const url = new URL(locationValue, 'https://example.invalid')
    if (handle) url.searchParams.set('view_product', handle)
    else url.searchParams.delete('view_product')
    return `${url.pathname}${url.search}${url.hash}`
  }

  const sectionLocation = (productUrl, sectionId = 'lg-quick-shop', origin = root?.location?.origin || 'https://example.invalid') => {
    const url = new URL(productUrl, origin)
    url.searchParams.set('section_id', sectionId)
    return url.href
  }

  const createBoundedCache = (limit = 6) => {
    const values = new Map()
    return {
      get (key) { return values.get(key) },
      set (key, value) {
        if (values.has(key)) values.delete(key)
        while (values.size >= limit) values.delete(values.keys().next().value)
        values.set(key, value)
      },
      has (key) { return values.has(key) },
      size () { return values.size }
    }
  }

  let activeInstance = null

  const isOpen = () => Boolean(activeInstance?.dialog?.open)

  if (root?.customElements && root.HTMLElement && !root.customElements.get('lg-quick-shop')) {
    class LGQuickShop extends root.HTMLElement {
      connectedCallback () {
        if (this.initialized) return
        this.initialized = true
        activeInstance = this
        this.dialog = this.querySelector('[data-lg-quickshop-dialog]')
        this.content = this.querySelector('[data-lg-quickshop-content]')
        this.status = this.querySelector('[data-lg-quickshop-status]')
        this.error = this.querySelector('[data-lg-quickshop-error]')
        this.errorLink = this.querySelector('[data-lg-quickshop-error-link]')
        this.position = this.querySelector('[data-lg-quickshop-position]')
        this.titleElement = this.querySelector('[data-lg-quickshop-title]')
        this.previous = this.querySelector('[data-lg-quickshop-previous]')
        this.next = this.querySelector('[data-lg-quickshop-next]')
        this.cache = createBoundedCache(6)
        this.selectionCache = createBoundedCache(20)
        this.pendingAddTitles = []
        this.prefetches = new Map()
        this.abortController = new AbortController()
        this.requestToken = 0
        const signal = this.abortController.signal

        this.querySelectorAll('[data-lg-quickshop-close]').forEach(button => {
          button.addEventListener('click', () => this.requestClose(), { signal })
        })
        this.querySelector('[data-lg-quickshop-retry]')?.addEventListener('click', () => this.loadCurrent(), { signal })
        this.previous?.addEventListener('click', () => this.move(-1), { signal })
        this.next?.addEventListener('click', () => this.move(1), { signal })

        this.dialog?.addEventListener('cancel', event => {
          event.preventDefault()
          this.requestClose()
        }, { signal })
        this.dialog?.addEventListener('pointerdown', event => {
          this.backdropPointerStarted = event.target === this.dialog
        }, { signal })
        this.dialog?.addEventListener('click', event => {
          if (event.target === this.dialog && this.backdropPointerStarted) this.requestClose()
          this.backdropPointerStarted = false
        }, { signal })

        root.addEventListener('lg:quickshop:open', event => this.open(event.detail), { signal })
        root.addEventListener('popstate', () => this.onPopState(), { signal })
        root.addEventListener('kt.collection.loaded', () => this.enableTriggers(), { signal })
        root.addEventListener('lg:finder:results', () => this.enableTriggers(), { signal })
        root.addEventListener('shopify:section:load', () => this.enableTriggers(), { signal })
        root.addEventListener('lg:variant:changed', event => this.onVariantChanged(event), { signal })
        root.addEventListener('lg:cart:updated', event => this.onCartUpdated(event), { signal })
        root.addEventListener('lg:cart:error', () => this.onCartError(), { signal })
        this.addEventListener('submit', event => this.capturePendingAdd(event), { capture: true, signal })

        this.enableTriggers()
        root.requestAnimationFrame(() => this.openFromLocation())
      }

      disconnectedCallback () {
        this.requestToken += 1
        this.detailAbort?.abort()
        this.abortController?.abort()
        if (activeInstance === this) activeInstance = null
        this.initialized = false
      }

      enableTriggers () {
        root.document.querySelectorAll('[data-lg-quickshop-trigger]').forEach(trigger => {
          trigger.hidden = false
          trigger.disabled = false
        })
      }

      detailFromTrigger (trigger) {
        const card = trigger?.closest('[data-lg-product-card]')
        const list = card?.closest('ul')
        if (!card || !list) return null
        const cards = Array.from(list.querySelectorAll('[data-lg-product-card]'))
          .filter(item => item.dataset.productUrl && item.dataset.productHandle)
        const index = cards.indexOf(card)
        if (index < 0) return null
        const gallery = card.querySelector('lg-card-gallery')
        return {
          version: 1,
          contextId: list.dataset.lgQuickshopContext || '',
          sourceRoute: `${root.location.pathname}${root.location.search}`,
          products: cards.map(item => ({ id: item.dataset.productId, handle: item.dataset.productHandle, url: item.dataset.productUrl })),
          index,
          productUrl: card.dataset.productUrl,
          productHandle: card.dataset.productHandle,
          initialMediaId: gallery?.querySelector('.lg-card-gallery__image')?.dataset.mediaId || gallery?.dataset.primaryMediaId || '',
          openerId: trigger.id || '',
          scrollX: root.scrollX,
          scrollY: root.scrollY
        }
      }

      openFromLocation () {
        const handle = new URL(root.location.href).searchParams.get('view_product')
        if (!handle) return
        const trigger = Array.from(root.document.querySelectorAll('[data-lg-product-card]'))
          .find(card => card.dataset.productHandle === handle)
          ?.querySelector('[data-lg-quickshop-trigger]')
        const detail = this.detailFromTrigger(trigger)
        if (detail) this.open(detail, { history: false, ownedHistory: root.history.state?.lgQuickShop === true })
      }

      async open (detail, options = {}) {
        if (!detail || detail.version !== 1) return
        const products = normalizeProducts(detail.products)
        if (!products.length) return
        const requestedIndex = products.findIndex(product => product.handle === detail.productHandle || product.url === detail.productUrl)
        this.context = {
          id: String(detail.contextId || ''),
          sourceRoute: String(detail.sourceRoute || `${root.location.pathname}${root.location.search}`),
          products,
          index: requestedIndex >= 0 ? requestedIndex : Math.max(0, Math.min(Number(detail.index) || 0, products.length - 1))
        }
        this.initialMediaId = String(detail.initialMediaId || '')
        this.openerId = String(detail.openerId || '')
        this.savedScroll = { x: Number(detail.scrollX) || 0, y: Number(detail.scrollY) || 0 }
        this.historyOwned = options.ownedHistory === true
        this.hidden = false
        if (this.dialog && !this.dialog.open) this.dialog.showModal()

        if (options.history !== false) {
          const product = this.context.products[this.context.index]
          const nextLocation = viewerLocation(root.location.href, product.handle)
          const alreadyViewer = new URL(root.location.href).searchParams.has('view_product')
          if (alreadyViewer) root.history.replaceState({ lgQuickShop: true, contextId: this.context.id }, '', nextLocation)
          else root.history.pushState({ lgQuickShop: true, contextId: this.context.id }, '', nextLocation)
          this.historyOwned = true
        }
        await this.loadCurrent(this.initialMediaId)
      }

      captureSelections () {
        const product = this.currentProduct()
        const form = this.content?.querySelector('form')
        if (!product || !form) return
        const values = {}
        form.querySelectorAll('input, select, textarea').forEach(field => {
          if (!field.name || ((field.type === 'radio' || field.type === 'checkbox') && !field.checked)) return
          values[field.name] = field.value
        })
        this.selectionCache.set(product.url, values)
      }

      restoreSelections () {
        const product = this.currentProduct()
        const form = this.content?.querySelector('form')
        const values = product && this.selectionCache.get(product.url)
        if (!form || !values) return
        let changedOption = null
        form.querySelectorAll('input, select, textarea').forEach(field => {
          if (!Object.hasOwn(values, field.name)) return
          if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === values[field.name]
          else field.value = values[field.name]
          if (field.matches('[data-product-option], [name^="option-"], [name^="options["]')) changedOption = field
        })
        changedOption?.dispatchEvent(new Event('change', { bubbles: true }))
      }

      currentProduct () {
        return this.context?.products?.[this.context.index] || null
      }

      async move (delta) {
        if (!this.context) return
        const nextIndex = this.context.index + delta
        if (nextIndex < 0 || nextIndex >= this.context.products.length) return
        this.captureSelections()
        this.context.index = nextIndex
        this.initialMediaId = ''
        const product = this.currentProduct()
        root.history.replaceState({ lgQuickShop: true, contextId: this.context.id }, '', viewerLocation(root.location.href, product.handle))
        await this.loadCurrent()
      }

      updateNavigation () {
        if (!this.context) return
        const current = this.context.index + 1
        const total = this.context.products.length
        if (this.position) this.position.textContent = `${current} of ${total}`
        if (this.previous) this.previous.disabled = current <= 1
        if (this.next) this.next.disabled = current >= total
      }

      async loadCurrent (initialMediaId = '') {
        const product = this.currentProduct()
        if (!product || !this.content) return
        const token = ++this.requestToken
        this.detailAbort?.abort()
        this.detailAbort = new AbortController()
        this.updateNavigation()
        this.error.hidden = true
        this.content.setAttribute('aria-busy', 'true')
        if (this.status) this.status.textContent = `Loading ${product.handle.replaceAll('-', ' ')}.`
        if (this.errorLink) this.errorLink.href = product.url

        try {
          const requestUrl = sectionLocation(product.url, this.dataset.sectionId || 'lg-quick-shop')
          let markup = this.cache.get(requestUrl)
          if (!markup && this.prefetches.has(requestUrl)) {
            await this.prefetches.get(requestUrl)
            markup = this.cache.get(requestUrl)
          }
          if (!markup) {
            const response = await root.fetch(requestUrl, {
              headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
              signal: this.detailAbort.signal
            })
            if (!response.ok) throw new Error(`Quick shop request failed: ${response.status}`)
            markup = await response.text()
            this.cache.set(requestUrl, markup)
          }
          if (token !== this.requestToken) return
          const parsed = new DOMParser().parseFromString(markup, 'text/html')
          const nextProduct = parsed.querySelector('[data-lg-quickshop-product]')
          if (!nextProduct) throw new Error('Quick shop response was empty')
          const importedProduct = root.document.importNode(nextProduct, true)
          try {
            this.content.replaceChildren(importedProduct)
          } catch (renderError) {
            if (!this.content.querySelector('[data-lg-quickshop-product]')) throw renderError
            root.console?.warn('Quick shop child initialization reported an error.', renderError)
          }
          this.content.removeAttribute('aria-busy')
          const title = this.content.querySelector('.lg-quick-shop-product__title')
          if (this.titleElement) this.titleElement.textContent = title?.textContent?.trim() || 'Quick shop'
          if (this.status) this.status.textContent = `${this.titleElement?.textContent || 'Design'} loaded.`
          try { this.restoreSelections() } catch (error) { root.console?.warn('Quick shop selection restore skipped.', error) }
          try { this.applyInitialMedia(initialMediaId) } catch (error) { root.console?.warn('Quick shop media handoff skipped.', error) }
          try {
            title?.setAttribute('tabindex', '-1')
            title?.focus({ preventScroll: true })
          } catch (error) { root.console?.warn('Quick shop heading focus skipped.', error) }
          try { this.prefetchAdjacent() } catch (error) { root.console?.warn('Quick shop prefetch skipped.', error) }
        } catch (error) {
          if (error.name === 'AbortError' || token !== this.requestToken) return
          root.console?.error('Quick shop details failed to load.', error)
          this.content.removeAttribute('aria-busy')
          this.error.hidden = false
          if (this.status) this.status.textContent = 'Quick shop details could not be loaded. Retry or open the full product page.'
          this.querySelector('[data-lg-quickshop-retry]')?.focus({ preventScroll: true })
        }
      }

      prefetchAdjacent () {
        const connection = root.navigator?.connection
        if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return
        const neighbor = this.context?.products?.[this.context.index + 1] || this.context?.products?.[this.context.index - 1]
        if (!neighbor) return
        const requestUrl = sectionLocation(neighbor.url, this.dataset.sectionId || 'lg-quick-shop')
        if (this.cache.has(requestUrl) || this.prefetches.has(requestUrl) || this.prefetches.size >= 2) return

        const pending = root.fetch(requestUrl, {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        }).then(async response => {
          if (!response.ok) return
          const markup = await response.text()
          if (markup.includes('data-lg-quickshop-product')) this.cache.set(requestUrl, markup)
        }).catch(() => {}).finally(() => this.prefetches.delete(requestUrl))
        this.prefetches.set(requestUrl, pending)
      }

      applyInitialMedia (mediaId) {
        if (!mediaId) return
        root.requestAnimationFrame(() => {
          const gallery = this.content?.querySelector('lg-product-gallery')
          const slides = Array.from(gallery?.querySelectorAll('[data-lg-gallery-media]') || [])
          const index = slides.findIndex(slide => String(slide.dataset.mediaId) === String(mediaId))
          if (index >= 0 && typeof gallery.show === 'function') gallery.show(index)
        })
      }

      onVariantChanged (event) {
        const productRoot = this.content?.querySelector('[data-lg-quickshop-product]')
        if (!productRoot || String(event.detail?.productId) !== String(productRoot.dataset.productId)) return
        const link = this.content.querySelector('.lg-quick-shop-product__canonical-link')
        if (!link || !event.detail.variantId) return
        const url = new URL(productRoot.dataset.productUrl, root.location.origin)
        url.searchParams.set('variant', event.detail.variantId)
        link.href = url.href
      }

      capturePendingAdd (event) {
        if (!event.target.closest('product-form')) return
        const productRoot = this.content?.querySelector('[data-lg-quickshop-product]')
        this.pendingAddTitles.push(productRoot?.dataset.productTitle || 'This set')
      }

      onCartUpdated (event) {
        if (!this.pendingAddTitles.length || event.detail?.action !== 'add' || event.detail?.source !== 'product') return
        const productTitle = this.pendingAddTitles.shift()
        if (this.status) this.status.textContent = `${productTitle} added to your bag.`
      }

      onCartError () {
        if (!this.pendingAddTitles.length) return
        const productTitle = this.pendingAddTitles.shift()
        if (this.status) this.status.textContent = `${productTitle} was not confirmed. Check your bag before retrying.`
      }

      requestClose () {
        if (this.historyOwned && new URL(root.location.href).searchParams.has('view_product')) {
          root.history.back()
          return
        }
        if (new URL(root.location.href).searchParams.has('view_product')) {
          root.history.replaceState({}, '', viewerLocation(root.location.href))
        }
        this.finishClose()
      }

      finishClose () {
        this.captureSelections()
        this.requestToken += 1
        this.detailAbort?.abort()
        if (this.dialog?.open) this.dialog.close()
        this.hidden = true
        this.content?.replaceChildren()
        if (this.status) this.status.textContent = ''
        if (this.error) this.error.hidden = true
        const opener = this.openerId ? root.document.getElementById(this.openerId) : null
        root.scrollTo(this.savedScroll?.x || 0, this.savedScroll?.y || 0)
        opener?.focus({ preventScroll: true })
        this.context = null
        this.historyOwned = false
      }

      onPopState () {
        const handle = new URL(root.location.href).searchParams.get('view_product')
        if (!handle) {
          if (isOpen()) this.finishClose()
          return
        }
        if (!isOpen()) {
          this.openFromLocation()
          return
        }
        const index = this.context?.products.findIndex(product => product.handle === handle) ?? -1
        if (index >= 0 && index !== this.context.index) {
          this.captureSelections()
          this.context.index = index
          this.loadCurrent()
        }
      }
    }

    root.customElements.define('lg-quick-shop', LGQuickShop)
  }

  return { version: 1, normalizeProducts, viewerLocation, sectionLocation, createBoundedCache, isOpen }
})
